import { FC } from 'react';
import { Controller } from 'react-hook-form';
import { Stack, TextField, Typography } from '@mui/material';
import { useDictionary } from '@cvt/hooks/useDictionary';

import { WordCount } from '@shared/components/WordCount';
import { CharacterCount } from '@shared/components/CharacterCount';
import { TimeEstimates } from '@shared/components/TimeEstimates';
import { LengthEstimates } from '@shared/components/LengthEstimates';
import { MAX_STORY_CONTENT_LENGTH } from '@cvt/helpers/validation';


export const StoryField: FC = () => {
  const dictionary = useDictionary();

  return (
    <Stack gap={3}>
      <Typography variant="body1" fontSize={30} fontWeight={600}>
        Full Story
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Write your complete story in prose format
      </Typography>

      <Controller
        name="content"
        rules={{
          required: dictionary.forms.validations.required,
          maxLength: {
            value: MAX_STORY_CONTENT_LENGTH,
            message: dictionary.forms.validations.maxLength(MAX_STORY_CONTENT_LENGTH),
          },
        }}
        render={({ field: { ref, ...field }, fieldState }) => (
          <TextField
            {...field}
            inputRef={ref}
            variant="outlined"
            color="primary"
            fullWidth
            multiline
            minRows={7}
            error={!!fieldState.error}
            helperText={(
              <Stack component="span">
                <span>{fieldState.error?.message}</span>
                <Stack component="span" direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="space-between" alignItems="center" width="100%" gap={2}>
                  <Stack component="span" direction="column" gap={1} alignItems="flex-start">
                    <Stack component="span" direction="row" gap={2} alignItems="center">
                      <WordCount text={field.value} fontWeight={500} display={{ xs: 'none', sm: 'inline' }}/>
                      <LengthEstimates length={field.value.length}/>
                    </Stack>
                    <TimeEstimates text={field.value} display={{ xs: 'none', sm: 'inline' }}/>
                  </Stack>

                  <Stack component="span" direction="column" gap={1} width={{ xs: '100%', sm: 'auto' }}>
                    <Stack component="span" direction="row" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
                      <WordCount text={field.value} fontWeight={500} display={{ xs: 'inline', sm: 'none' }}/>
                      <CharacterCount
                        text={field.value}
                        maxLength={MAX_STORY_CONTENT_LENGTH}
                        flexShrink={0}
                        mediumCount={120}
                        preferredLength={150}
                        fontWeight={500}
                      />
                    </Stack>
                    <TimeEstimates text={field.value} display={{ xs: 'inline', sm: 'none' }}/>
                  </Stack>
                </Stack>
              </Stack>
            )}
            placeholder={dictionary.forms.story.placeholder}
          />
        )}
      />
    </Stack>
  );
};
